import type { LocationQuery, RouteParams } from 'vue-router'

export const CREATE_V2_QUERY_FROM_MAP = 'map'

export type MapLngLat = {
  lng: number
  lat: number
  zoom?: number
}

function queryString(value: LocationQuery[string]): string {
  if (value == null) return ''
  return Array.isArray(value) ? String(value[0] ?? '') : String(value)
}

/** Positive integer from route query or param (handles `string[]` from Vue Router). */
export function parsePositiveRouteInt(value: LocationQuery[string] | unknown): number | null {
  if (value == null) return null
  const raw = Array.isArray(value) ? value[0] : value
  const n = Number(String(raw).trim())
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Map-object id for root `POST /inventory/objects` parent_id when entering create-v2 from the map.
 * Order: explicit `parent_id` query → `group_object_id` query → route `object_id` (group).
 */
export function resolveMapEntryParentMapId(
  query: LocationQuery,
  params: RouteParams,
): number | null {
  const fromParentQuery = parsePositiveRouteInt(query.parent_id)
  if (fromParentQuery != null) return fromParentQuery
  const fromGroupQuery = parsePositiveRouteInt(query.group_object_id)
  if (fromGroupQuery != null) return fromGroupQuery
  return parsePositiveRouteInt(params.object_id)
}

export function isCreateV2FromMap(query: LocationQuery): boolean {
  return queryString(query.from) === CREATE_V2_QUERY_FROM_MAP
}

export function parseLngLatFromQuery(query: LocationQuery): MapLngLat | null {
  const lngRaw = queryString(query.lng).trim()
  const latRaw = queryString(query.lat).trim()
  if (!lngRaw || !latRaw) return null

  const lng = Number(lngRaw)
  const lat = Number(latRaw)
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null

  const zoomRaw = queryString(query.zoom).trim()
  const zoom = zoomRaw ? Number(zoomRaw) : undefined
  return {
    lng,
    lat,
    zoom: Number.isFinite(zoom) && zoom! > 0 ? zoom : undefined,
  }
}

export function mapRouteNameForObjectType(type: string): string {
  const t = String(type || 'groups').trim() || 'groups'
  return `objects.${t}.map`
}

export function buildMapReturnQuery(options: {
  lng: number
  lat: number
  zoom?: number
  ids?: number[]
}): Record<string, string> {
  const q: Record<string, string> = {
    lng: String(options.lng),
    lat: String(options.lat),
  }
  if (options.zoom != null && Number.isFinite(options.zoom) && options.zoom > 0) {
    q.zoom = String(options.zoom)
  }
  if (options.ids?.length) {
    q.ids = options.ids.join(',')
    q.focus = '1'
  }
  return q
}

export function buildCreateV2FromMapQuery(options: {
  lng: number
  lat: number
  zoom: number
  parentId: number
  /** Group map id (`route.params.object_id`) — fallback if `parent_id` is lost from the URL. */
  groupObjectId: number
  returnMapType: string
  /** Full line/polygon vertices from map draw; omitted for point objects. */
  geometry?: number[][]
}): Record<string, string> {
  const q: Record<string, string> = {
    from: CREATE_V2_QUERY_FROM_MAP,
    lng: String(options.lng),
    lat: String(options.lat),
    zoom: String(options.zoom),
    parent_id: String(options.parentId),
    return_map: String(options.returnMapType),
  }
  if (Number.isFinite(options.groupObjectId) && options.groupObjectId > 0) {
    q.group_object_id = String(options.groupObjectId)
  }
  if (options.geometry?.length) {
    q.geom = serializeMapGeometryToQuery(options.geometry)
  }
  return q
}

/** Normalize map draw payload to `[][]float64` (point, line, or polygon ring). */
export function normalizeMapDrawCoordinates(coordinates: unknown): number[][] | null {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null

  if (
    coordinates.length === 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  ) {
    const lng = Number(coordinates[0])
    const lat = Number(coordinates[1])
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
    return [[lng, lat]]
  }

  const pairs: number[][] = []
  for (const item of coordinates) {
    if (!Array.isArray(item) || item.length < 2) continue
    const lng = Number(item[0])
    const lat = Number(item[1])
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
    pairs.push([lng, lat])
  }
  return pairs.length > 0 ? pairs : null
}

export function serializeMapGeometryToQuery(geometry: number[][]): string {
  return encodeURIComponent(JSON.stringify(geometry))
}

export function parseMapGeometryFromQuery(query: LocationQuery): number[][] | null {
  const raw = queryString(query.geom).trim()
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw))
    return normalizeMapDrawCoordinates(parsed)
  } catch {
    return null
  }
}

/** Normalize map click payload to WGS84 `[lng, lat]`. */
export function clickCoordsToLngLat(coordinates: unknown): MapLngLat | null {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null
  const first = coordinates[0]
  const pair: unknown = Array.isArray(first) && typeof first[0] === 'number' ? first : coordinates
  if (!Array.isArray(pair) || pair.length < 2) return null
  const lng = Number(pair[0])
  const lat = Number(pair[1])
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  return { lng, lat }
}

export function resolveMapRouteParams(params: RouteParams): {
  type: string
  objectId: string
} {
  return {
    type: String(params.type ?? 'groups'),
    objectId: String(params.object_id ?? ''),
  }
}
